import { extend, $$, offset, removeEvent, addEvent } from './HelperFunctions'

export class Parallax {
  constructor (settings, selectors, classes) {
    this.settings = extend({
      type: 'class', // Add class or move with "transform: translate(x);" class | move | scale | rotate
      orientation: 'down', // up | right | down | left
      distance: '20%', // Percent
      scale: '1', // Default: 1
      rotateStart: '0', // Default: 0
      rotateEnd: '0', // Default: 0
      minScreenWidth: 0,
      maxScreenWidth: 9999,
      minScreenHeight: 0,
      maxScreenHeight: 9999,
      lastScrollPosition: window.scrollY,
      parallaxActiveClassStart: 35, // 0-100
      parallaxActiveClassEnd: 65 // 0-100
    }, settings)

    this.selectors = extend({
      parallax: '[data-js-parallax]',
      item: '[data-js-parallax-item]',
      imgToLoad: '.parallax__load-image'
    }, selectors)

    this.classes = extend({
      active: 'parallax--active',
      imgToLoad: 'parallax__load-image',
      animateFromTop: 'parallax--animate-from-top',
      animateFromBottom: 'parallax--animate-from-bottom'
    }, classes)
  }

  init () {
    this.setAnchors()
    this.addEvents()
    this.setParallax()
  }

  addEvents () {
    const that = this
    let resizeTimer

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer)

      resizeTimer = setTimeout(function () {
        that.setAnchors()
        that.setParallax()
      }, 250)
    })

    window.addEventListener('scroll', () => {
      that.setParallax()
      that.settings.lastScrollPosition = window.scrollY
    })
  }

  setAnchors () {
    const that = this
    const parallax = $$(that.selectors.parallax)

    if (parallax) {
      [].forEach.call(parallax, (parallaxElement) => {
        const imgToLoad = $$(that.selectors.imgToLoad, parallaxElement)
        if (imgToLoad.length > 0) {
          [].forEach.call(imgToLoad, (imgToLoadElement) => {
            removeEvent(imgToLoadElement, 'load')
            const loadEvent = addEvent(imgToLoadElement, 'load', function () {
              parallaxElement.dataset.jsParallaxScrollStart = offset(parallaxElement).top - (window.innerHeight * 1.5)
              parallaxElement.dataset.jsParallaxScrollEnd = (offset(parallaxElement).top + parallaxElement.getBoundingClientRect().height) + (window.innerHeight * 0.5)
            })

            if (imgToLoadElement.complete) {
              imgToLoadElement.classList.remove(that.classes.imgToLoad)
              imgToLoadElement.dispatchEvent(loadEvent)
            }
          })
        } else {
          parallaxElement.dataset.jsParallaxScrollStart = offset(parallaxElement).top - (window.innerHeight * 1.5)
          parallaxElement.dataset.jsParallaxScrollEnd = (offset(parallaxElement).top + parallaxElement.getBoundingClientRect().height) + (window.innerHeight * 0.5)
        }
      })
    }
  }

  setParallax () {
    const that = this
    const parallax = $$(that.selectors.parallax)

    if (parallax) {
      [].forEach.call(parallax, (parallaxElement) => {
        that.animate(parallaxElement)
      })
    }
  }

  animate (parallax) {
    const that = this
    const scrollTop = window.scrollY
    const parallaxScrollStart = parallax.dataset.jsParallaxScrollStart
    const parallaxScrollEnd = parallax.dataset.jsParallaxScrollEnd || parallaxScrollStart
    const calculatedScrollTop = parallaxScrollStart < 0 ? scrollTop + Math.abs(parallaxScrollStart) : scrollTop
    const parallaxMinScreenWidth = parallax.dataset.jsParallaxMinScreenWidth || that.settings.minScreenWidth
    const parallaxMaxScreenWidth = parallax.dataset.jsParallaxMaxScreenWidth || that.settings.maxScreenWidth
    const parallaxMinScreenHeight = parallax.dataset.jsParallaxMinScreenHeight || that.settings.minScreenHeight
    const parallaxMaxScreenHeight = parallax.dataset.jsParallaxMaxScreenHeight || that.settings.maxScreenHeight
    let scrollDistance, scrollDistancePercent
    const items = $$(that.selectors.item, parallax)

    // viewport restrictions and check if viewport shows the paralax
    if (window.innerWidth >= parallaxMinScreenWidth &&
      window.innerWidth <= parallaxMaxScreenWidth &&
      window.innerHeight >= parallaxMinScreenHeight &&
      window.innerHeight <= parallaxMaxScreenHeight &&
      ((((window.scrollY + window.innerHeight) <= parallaxScrollEnd) &&
          (window.scrollY + window.innerHeight) >= parallaxScrollStart) ||
        ((window.scrollY >= parallaxScrollStart) &&
          window.scrollY <= parallaxScrollEnd))) {
      if (!parallax.classList.contains(that.classes.active)) {
        parallax.classList.add(that.classes.active)

        if (that.settings.lastScrollPosition < window.scrollY) {
          parallax.dataset.jsScrolledDirection = 'down'
        } else {
          parallax.dataset.jsScrolledDirection = 'up'
        }
      }

      // calculate scrolled distance
      scrollDistance = parallaxScrollEnd - parallaxScrollStart
      if (scrollTop < parallaxScrollStart) {
        scrollDistancePercent = 0
      } else if (scrollTop > parallaxScrollEnd) {
        scrollDistancePercent = 100
      } else {
        if (parallaxScrollStart < 0) {
          scrollDistancePercent = (calculatedScrollTop / scrollDistance) * 100
        } else {
          scrollDistancePercent = ((calculatedScrollTop - parallaxScrollStart) / scrollDistance) * 100
        }
      }

      if (items) {
        [].forEach.call(items, (item) => {
          const parallaxType = item.dataset.jsParallaxType || that.settings.type
          const itemMinScreenWidth = item.dataset.jsParallaxMinScreenWidth || that.settings.minScreenWidth
          const itemMaxScreenWidth = item.dataset.jsParallaxMaxScreenWidth || that.settings.maxScreenWidth
          const itemMinScreenHeight = item.dataset.jsParallaxMinScreenHeight || that.settings.minScreenHeight
          const itemMaxScreenHeight = item.dataset.jsParallaxMaxScreenHeight || that.settings.maxScreenHeight
          const itemTransform = {}

          if (window.innerWidth >= itemMinScreenWidth &&
            window.innerWidth <= itemMaxScreenWidth &&
            window.innerHeight >= itemMinScreenHeight &&
            window.innerHeight <= itemMaxScreenHeight) {
            if (parallaxType.includes('class')) {
              const parallaxActiveClassStart = item.dataset.jsParallaxActiveClassStart || that.settings.parallaxActiveClassStart
              const parallaxActiveClassEnd = item.dataset.jsParallaxActiveClassEnd || that.settings.parallaxActiveClassEnd
              if (Math.round(scrollDistancePercent) > parallaxActiveClassStart && Math.round(scrollDistancePercent) < parallaxActiveClassEnd) {
                if (parallax.dataset.jsScrolledDirection === 'down') {
                  item.classList.add(that.classes.animateFromTop)
                } else {
                  item.classList.add(that.classes.animateFromBottom)
                }
              } else {
                item.classList.remove(that.classes.animateFromTop, that.classes.animateFromBottom)
              }
            }

            if (parallaxType.includes('scale')) {
              const parallaxScale = item.dataset.jsParallaxScale || that.settings.scale
              itemTransform.scale = 'scale(' + ((((parallaxScale - 1) / 100) * scrollDistancePercent) + 1) + ')'
            }

            if (parallaxType.includes('rotate')) {
              const parallaxRotateStart = parseInt(item.dataset.jsParallaxRotateStart) || parseInt(that.settings.rotateStart)
              const parallaxRotateEnd = parseInt(item.dataset.jsParallaxRotateEnd) || parseInt(that.settings.rotateEnd)

              if (parallaxRotateStart > parallaxRotateEnd) {
                const parallaxRotateDifference = parallaxRotateStart - parallaxRotateEnd
                itemTransform.rotate = 'rotate(' + (parallaxRotateStart - (parallaxRotateDifference / 100) * scrollDistancePercent) + 'deg)'
              } else if (parallaxRotateStart < parallaxRotateEnd) {
                const parallaxRotateDifference = parallaxRotateEnd - parallaxRotateStart
                itemTransform.rotate = 'rotate(' + (parallaxRotateStart + (parallaxRotateDifference / 100) * scrollDistancePercent) + 'deg)'
              }
            }

            if (parallaxType.includes('move')) {
              const parallaxOrientation = item.dataset.jsParallaxOrientation || that.settings.orientation
              const parallaxScale = item.dataset.jsParallaxScale || that.settings.scale
              let parallaxDistance = that.getScrollDistance(item.dataset.jsParallaxDistance || that.settings.distance, item, parallaxOrientation)

              if (parallaxOrientation === 'down') {
                if (!parallaxDistance) {
                  parallaxDistance = item.getBoundingClientRect().height / 4
                }

                itemTransform.translate = 'translateY(' + (((parallaxDistance / 100) * scrollDistancePercent) - (parallaxDistance / 2)) + 'px)'
              } else if (parallaxOrientation === 'up') {
                if (!parallaxDistance) {
                  parallaxDistance = item.getBoundingClientRect().height / 4
                }

                itemTransform.translate = 'translateY(' + (((parallaxDistance / 100) * (100 - scrollDistancePercent)) - (parallaxDistance / 2)) + 'px)'
              } else if (parallaxOrientation === 'left') {
                if (!parallaxDistance) {
                  parallaxDistance = item.getBoundingClientRect().width / 4
                }

                itemTransform.translate = 'translateX(' + (((parallaxDistance / 100) * (100 - scrollDistancePercent)) - (parallaxDistance / 2)) + 'px)'
              } else if (parallaxOrientation === 'right') {
                if (!parallaxDistance) {
                  parallaxDistance = item.getBoundingClientRect().width / 4
                }

                itemTransform.translate = 'translateX(' + (((parallaxDistance / 100) * scrollDistancePercent) - (parallaxDistance / 2)) + 'px)'
              }

              if (parallaxScale !== '1' && !itemTransform.scale) {
                itemTransform.scale = 'scale(' + parallaxScale + ')'
              }
            }

            if (Object.keys(itemTransform).length > 0) {
              item.style.transform = Object.keys(itemTransform).map(function (key) {
                return itemTransform[key]
              }).join(' ')
            }
          }
        })
      }
    } else {
      if (parallax.classList.contains(that.classes.active)) {
        if (items) {
          [].forEach.call(items, (item) => {
            item.style.transform = ''
            item.classList.remove(that.classes.animateFromTop, that.classes.animateFromBottom)
          })
        }

        parallax.classList.remove(that.classes.active)
      }
    }
  }

  getScrollDistance (value, element, orientation) {
    const regExPercent = /\d+(\\.\\d+)?%/g

    if (typeof value === 'string') {
      if (value.indexOf('%')) {
        const percentArr = value.match(regExPercent)

        if (percentArr.length > 0) {
          const percentToNumber = parseFloat(percentArr[0])

          if (orientation === 'down' || orientation === 'up') {
            return ((element.getBoundingClientRect().height * percentToNumber) / 100)
          } else {
            return ((element.getBoundingClientRect().width * percentToNumber) / 100)
          }
        }
      }
    }

    return value
  }
}

export default Parallax
